import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Clock } from 'lucide-react';
function useCountdown(hours: number) {
  const [endTime] = useState(() => {
    const stored = localStorage.getItem('cta-countdown-end');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (parsed > Date.now()) return parsed;
    }
    const end = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem('cta-countdown-end', String(end));
    return end;
  });
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, endTime - Date.now()));
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  const totalSeconds = Math.floor(timeLeft / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor(totalSeconds % 3600 / 60);
  const s = totalSeconds % 60;
  return {
    hours: h,
    minutes: m,
    seconds: s,
    isExpired: timeLeft <= 0
  };
}
export function CTASection() {
  const countdown = useCountdown(5);
  return <section id="cta-section" className="py-24 bg-white relative overflow-hidden" data-id="element-1">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-emerald-50/50 rounded-full blur-3xl -z-10" data-id="element-2"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-3">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl shadow-emerald-900/10 border border-emerald-100" data-id="element-4">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6" data-id="element-5">
            Recupera tu ligereza y tu confianza hoy mismo
          </h2>
          <p className="text-xl text-stone-600 mb-8" data-id="element-6">
            por una fracción de lo que cuesta una consulta nutricional privada.
          </p>

          {/* Countdown Timer */}
          {!countdown.isExpired && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-7">
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-5 py-2 mb-4" data-id="element-8">
                <Clock className="w-4 h-4 text-amber-600" data-id="element-9" />
                <span className="text-sm font-semibold text-amber-800" data-id="element-10">
                  Oferta válida por tiempo limitado
                </span>
              </div>
              <div className="flex items-center justify-center gap-3" data-id="element-11">
                <div className="bg-stone-900 text-white rounded-xl px-4 py-3 min-w-[72px]" data-id="element-12">
                  <span className="text-2xl md:text-3xl font-bold tabular-nums" data-id="element-13">
                    {String(countdown.hours).padStart(2, '0')}
                  </span>
                  <span className="block text-xs text-stone-400 uppercase tracking-wider mt-1" data-id="element-14">
                    Horas
                  </span>
                </div>
                <span className="text-2xl font-bold text-stone-300" data-id="element-15">:</span>
                <div className="bg-stone-900 text-white rounded-xl px-4 py-3 min-w-[72px]" data-id="element-16">
                  <span className="text-2xl md:text-3xl font-bold tabular-nums" data-id="element-17">
                    {String(countdown.minutes).padStart(2, '0')}
                  </span>
                  <span className="block text-xs text-stone-400 uppercase tracking-wider mt-1" data-id="element-18">
                    Min
                  </span>
                </div>
                <span className="text-2xl font-bold text-stone-300" data-id="element-19">:</span>
                <div className="bg-stone-900 text-white rounded-xl px-4 py-3 min-w-[72px]" data-id="element-20">
                  <span className="text-2xl md:text-3xl font-bold tabular-nums" data-id="element-21">
                    {String(countdown.seconds).padStart(2, '0')}
                  </span>
                  <span className="block text-xs text-stone-400 uppercase tracking-wider mt-1" data-id="element-22">
                    Seg
                  </span>
                </div>
              </div>
            </motion.div>}

          {/* Pricing */}
          <div className="mb-10" data-id="element-23">
            <span className="block text-sm font-bold text-amber-500 uppercase tracking-wider mb-3" data-id="element-24">
              Precio Especial de Lanzamiento
            </span>
            <div className="flex items-center justify-center gap-3 mb-2" data-id="element-25">
              <span className="text-2xl md:text-3xl text-stone-400 line-through font-semibold" data-id="element-26">
                $25.000
              </span>
              <span className="bg-red-100 text-red-700 font-bold text-sm px-3 py-1 rounded-full" data-id="element-27">
                -50% OFF
              </span>
            </div>
            <div className="text-5xl md:text-6xl font-extrabold text-emerald-600" data-id="element-28">
              $12.500{' '}
              <span className="text-2xl text-stone-400 font-medium" data-id="element-29">ARS</span>
            </div>
          </div>

          <Button size="lg" href="https://www.rutinavital.com/cart/47900606857460:1" className="w-full md:w-auto md:min-w-[320px] mb-6 text-lg py-5" data-id="element-30">
            QUIERO DESINFLAMARME AHORA
          </Button>

          <div className="flex items-center justify-center text-sm text-stone-500 mt-6" data-id="element-31">
            <ShieldCheck className="w-5 h-5 text-emerald-500 mr-2" data-id="element-32" />
            <span data-id="element-33">
              Pago seguro mediante Mercado Pago o Tarjeta de Crédito. Acceso
              instantáneo.
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}